import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from '../../features/auth/authSlice';
import { useGetUserDataQuery, useUpdateUserProfileMutation } from '../../features/api/apiSlice';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import Header from '../../components/ui/Header'; // Import Header
import Footer from '../../components/ui/Footer'; // Import Footer
import Breadcrumbs from '../../components/ui/Breadcrumbs'; // Import Breadcrumbs

const UserProfile = () => {
  const token = useSelector(selectCurrentToken);
  const { data: userData, isLoading: isUserDataLoading, isError: isUserDataError, refetch: getUserData } = useGetUserDataQuery();
  const [updateUserProfile, { isLoading: isUpdatingProfile, isSuccess: isUpdateSuccess, isError: isUpdateError }] = useUpdateUserProfileMutation();

  const showToast = useToast(); // Initialize useToast hook

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    address: '',
    about_me: '',
    facebook_id: '',
    twiiter_id: '',
    instagram_id: '',
    pintrest_id: '',
    latitude: '',
    longitude: '',
    city: '',
    state: '',
    country: '',
  });

  useEffect(() => {
    if (token) {
      getUserData();
    }
  }, [token, getUserData]);

  useEffect(() => {
    if (userData && userData.data) {
      setFormData({
        name: userData.data.name || '',
        email: userData.data.email || '',
        mobile: userData.data.mobile || '',
        address: userData.data.address || '',
        about_me: userData.data.about_me || '',
        facebook_id: userData.data.facebook_id || '',
        twiiter_id: userData.data.twiiter_id || '',
        instagram_id: userData.data.instagram_id || '',
        pintrest_id: userData.data.pintrest_id || '',
        latitude: userData.data.latitude || '',
        longitude: userData.data.longitude || '',
        city: userData.data.city || '',
        state: userData.data.state || '',
        country: userData.data.country || '',
      });
    }
  }, [userData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateUserProfile(formData).unwrap();
      showToast('Profile updated successfully!', 'success');
    } catch (error) {
      showToast('Failed to update profile.', 'error');
      console.error('Failed to update profile:', error);
    }
  };

  if (isUserDataLoading) return <div>Loading profile data...</div>;
  if (isUserDataError) return <div>Error loading profile data.</div>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <Breadcrumbs />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-6">My Profile</h1>
          <div className="bg-card rounded-lg shadow-md p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                    disabled
                  />
                </div>
                <div>
                  <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">Mobile</label>
                  <Input
                    type="text"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address</label>
                  <Input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">City</label>
                  <Input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700">State</label>
                  <Input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
                  <Input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="latitude" className="block text-sm font-medium text-gray-700">Latitude</label>
                  <Input
                    type="text"
                    id="latitude"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="longitude" className="block text-sm font-medium text-gray-700">Longitude</label>
                  <Input
                    type="text"
                    id="longitude"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="facebook_id" className="block text-sm font-medium text-gray-700">Facebook ID</label>
                  <Input
                    type="text"
                    id="facebook_id"
                    name="facebook_id"
                    value={formData.facebook_id}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="twiiter_id" className="block text-sm font-medium text-gray-700">Twitter ID</label>
                  <Input
                    type="text"
                    id="twiiter_id"
                    name="twiiter_id"
                    value={formData.twiiter_id}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="instagram_id" className="block text-sm font-medium text-gray-700">Instagram ID</label>
                  <Input
                    type="text"
                    id="instagram_id"
                    name="instagram_id"
                    value={formData.instagram_id}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
                <div>
                  <label htmlFor="pintrest_id" className="block text-sm font-medium text-gray-700">Pinterest ID</label>
                  <Input
                    type="text"
                    id="pintrest_id"
                    name="pintrest_id"
                    value={formData.pintrest_id}
                    onChange={handleChange}
                    className="mt-1 block w-full"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="about_me" className="block text-sm font-medium text-gray-700">About Me</label>
                <textarea
                  id="about_me"
                  name="about_me"
                  value={formData.about_me}
                  onChange={handleChange}
                  rows="3"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                ></textarea>
              </div>
              <Button type="submit" disabled={isUpdatingProfile} className="w-full">
                {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
              </Button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserProfile;
